import { debounce } from '@zg-rentals/zdash';
import type { ComboboxOptionType } from '@zillow/constellation-10';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery } from '@zg-rentals/react-query5';
import { useSatelliteWebLandlordPropertyHandler_getAddressDetails } from '@zg-rentals/ts-satellite-client-react-query';
import { logError } from '@zg-rentals/log-error';
import { InboxPageContext } from '../contexts/InboxPageContextProvider';
import formatPropertyListings from '../utils/formatPropertyListings';
import useDevices from './useDevices';
import getPropertySearchQueryOptions from './propertySearchQueryOptions';

export type { PropertyListingItem } from '../utils/formatPropertyListings';

export type UpdatePathWithListingAlias = (listingAlias: string) => void;

type UsePropertyFiltersArgs = {
  defaultListingAliasOnLoad?: string;
  onPropertySelect?: (selectedListingAlias: string) => void;
  resetPropertyFilter?: () => void;
  updatePathWithListingAlias: UpdatePathWithListingAlias;
};

// Filter special characters in property search input, but include characters such as - /
// since they can be found in the property name
const filterSpecialCharacters = (str: string) => (str ?? '').trim().replace(/[^a-zA-Z0-9-/ ]/g, '');

const ERROR_FETCHING_PROPERTY_LISTINGS =
  'Sorry, we are having trouble getting property listings to search by. Please refresh the page and try again.';
const ERROR_FETCHING_PROPERTY_ADDRESS = 'Sorry, we were unable to filter by that property. Please try a new search.';

/**
 * @param defaultListingAliasOnLoad listingAlias to set on page load
 * @param onPropertySelect Optional callback to fire when a property is selected (e.g., for clickstream events)
 * @param resetPropertyFilter Function to remove selected listingAlias in the URL if user input is empty or API call errors
 * @param updatePathWithListingAlias Function to update the URL with selected listingAlias
 * @description Helper functions to handle filtering a list of properties based on user input or selection
 * and updating the URL with selected property listingAlias
 */
export default ({
  defaultListingAliasOnLoad,
  onPropertySelect,
  resetPropertyFilter,
  updatePathWithListingAlias,
}: UsePropertyFiltersArgs) => {
  const { listingAlias } = useContext(InboxPageContext);
  const { isMdAndBelow } = useDevices();

  const [searchText, setSearchText] = useState('');
  const [defaultListingAddress, setDefaultListingAddress] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const {
    data: propertyListingData,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading: isPropertyListingsDataLoading,
    error: propertyListingsError,
  } = useInfiniteQuery({
    ...getPropertySearchQueryOptions(debouncedSearchText),
    placeholderData: (previousData) => previousData, // Keep previous data while loading new query
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    refetchOnWindowFocus: true, // Refetch when user comes back to tab (if stale)
    retry: 2, // Retry failed requests twice
  });

  // Handle errors
  useEffect(() => {
    if (propertyListingsError) {
      setErrorMessage(ERROR_FETCHING_PROPERTY_LISTINGS);
      logError({
        error: propertyListingsError,
        context: {
          details: 'PropertyFilterHook - Error fetching property listings',
          listingAlias,
          searchText,
          debouncedSearchText,
        },
      });
    } else {
      // Clear a stale listings error once a new/successful fetch resolves (don't clobber an address error).
      setErrorMessage((current) => (current === ERROR_FETCHING_PROPERTY_LISTINGS ? '' : current));
    }
  }, [propertyListingsError, listingAlias, searchText, debouncedSearchText]);

  // Flatten all pages and format listings
  const propertyListings = useMemo(() => {
    if (!propertyListingData?.pages) return [];

    const allListings = propertyListingData.pages.flatMap((page) => page.listings);
    return formatPropertyListings(allListings, isMdAndBelow);
  }, [propertyListingData, isMdAndBelow]);

  const hasMoreToPaginate = hasNextPage ?? false;

  // Search pending: the debounce hasn't caught up to the typed term, or that term's query (not pagination)
  // is fetching. Gated on a non-empty term so a background refetch (e.g. window refocus) of the full
  // list doesn't render the search skeleton when nobody is typing.
  const normalizedSearchText = filterSpecialCharacters(searchText);
  const isSearchInFlight =
    normalizedSearchText !== debouncedSearchText || (normalizedSearchText !== '' && isFetching && !isFetchingNextPage);

  const isUrlListingInLoadedOptions = useMemo(
    () =>
      defaultListingAliasOnLoad
        ? propertyListings.some((listing) => listing.value === defaultListingAliasOnLoad)
        : false,
    [defaultListingAliasOnLoad, propertyListings],
  );

  const shouldFetchAddressForUrlListing =
    Boolean(defaultListingAliasOnLoad) && propertyListings.length > 0 && !isUrlListingInLoadedOptions;

  const {
    data: propertyAddressData,
    isLoading: isPropertyAddressLoading,
    error: propertyAddressError,
  } = useSatelliteWebLandlordPropertyHandler_getAddressDetails(
    { listingAlias: defaultListingAliasOnLoad ?? '' },
    {
      enabled: shouldFetchAddressForUrlListing,
      // Returning to the tab must not re-issue getAddressDetails for a listing the user can't
      // access (it would re-hit the 403 — an errored query is immediately stale). SLW's query
      // provider only sets `staleTime`, so the React Query default (`refetchOnWindowFocus: true`)
      // would otherwise apply.
      refetchOnWindowFocus: false,
      meta: {
        context: {
          api: 'getAddressDetails',
          details: 'PropertyFilterHook - Error fetching property address for listing',
          listingAlias,
        },
      },
    },
  );

  const listingAddress = propertyAddressData?.displayAddress ?? '';
  const hasCheckedForPropertyLabelOnLoad = useRef(false);

  useEffect(() => {
    if (listingAddress) {
      setDefaultListingAddress(listingAddress);
    }
  }, [listingAddress]);

  useEffect(() => {
    if (defaultListingAliasOnLoad && propertyListings.length > 0) {
      hasCheckedForPropertyLabelOnLoad.current = true;
    }
  }, [defaultListingAliasOnLoad, propertyListings.length]);

  useEffect(() => {
    if (!propertyAddressError) return;
    if (resetPropertyFilter) {
      resetPropertyFilter();
    }
    setErrorMessage(ERROR_FETCHING_PROPERTY_ADDRESS);
  }, [propertyAddressError, resetPropertyFilter]);

  const debouncedSearch = useRef(
    debounce((inputSearchText: string) => {
      const filteredSearchText = filterSpecialCharacters(inputSearchText);
      setDebouncedSearchText(filteredSearchText);
    }, 500),
  ).current;

  useEffect(() => {
    // When the component unmounts, the search request could
    // still be in progress, so we cancel the debounced function
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleSelectProperty = (selectedPropertyListingItem: ComboboxOptionType) => {
    setDefaultListingAddress('');
    setErrorMessage('');

    if (typeof selectedPropertyListingItem === 'object') {
      updatePathWithListingAlias(selectedPropertyListingItem.value);

      // Fire optional callback (e.g., for clickstream events)
      if (onPropertySelect) {
        onPropertySelect(selectedPropertyListingItem.value);
      }
    }
  };

  const handleChange = (inputSearchText = '', event: React.SyntheticEvent, reason: string) => {
    // We only run the search to filter properties as the user types,
    // and not when a property is selected
    if ((reason !== 'clear' && event.type === 'click') || ('key' in event && event.key === 'Enter')) {
      return;
    }

    setSearchText(inputSearchText);

    if (errorMessage) {
      setErrorMessage('');
    }

    if (!inputSearchText) {
      setDefaultListingAddress(''); // Clear the default listing address
      if (resetPropertyFilter) {
        resetPropertyFilter();
      }
    }

    debouncedSearch(inputSearchText);
  };

  // Lets consumers drive the debounced search directly, without handleChange's click/Enter-selection guards.
  // Memoized so a consumer that wraps it in useCallback stays referentially stable.
  const searchProperties = useCallback(
    (inputSearchText: string) => {
      setSearchText(inputSearchText);
      // Clearing restores the full list immediately instead of after the debounce.
      if (!inputSearchText) {
        debouncedSearch.cancel();
        setDebouncedSearchText('');
        return;
      }
      debouncedSearch(inputSearchText);
    },
    [debouncedSearch],
  );

  const handleScroll = async (isLastPropertyVisible: boolean) => {
    if (isLastPropertyVisible && hasMoreToPaginate && !isFetchingNextPage) {
      try {
        await fetchNextPage();
      } catch (error) {
        logError({ error, context: 'PropertyFilterHook - Error fetching more property listings' });
      }
    }
  };

  return {
    defaultListingAddress,
    errorMessage,
    handleChange,
    handleScroll,
    handleSelectProperty,
    hasMoreToPaginate,
    isUrlListingInLoadedOptions,
    isLoadingSearchResults: isPropertyListingsDataLoading || isFetchingNextPage,
    isSearchInFlight,
    propertyListings,
    searchProperties,
    searchText,
    shouldShowSkeleton:
      defaultListingAliasOnLoad &&
      (isPropertyListingsDataLoading || isPropertyAddressLoading) &&
      hasCheckedForPropertyLabelOnLoad,
  };
};
